import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { ArrowForwardIos, ArrowBackIos } from '@mui/icons-material';
import { FC, useEffect, useState } from 'react';

interface ICarousel {
  children: any;
  show: number;
  infiniteLoop?: boolean;
  handleCurrentIndex?: (val: number) => void;
  currentItemIndex?: number;
}

export const Carousel: FC<ICarousel> = ({ children, show, infiniteLoop, handleCurrentIndex, currentItemIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(currentItemIndex);
  const [length, setLength] = useState(children.length);
  const [isRepeating, setIsRepeating] = useState(infiniteLoop && children.length > show);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [touchPosition, setTouchPosition] = useState(null);
  // Set the length to match current children from props
  useEffect(() => {
    setLength(children.length);
    handleCurrentIndex(currentIndex);
    setIsRepeating(infiniteLoop && children.length > show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, show]);

  useEffect(() => {
    if (isRepeating) {
      if (currentIndex === show || currentIndex === length) {
        setTransitionEnabled(true);
      }
    }
  }, [currentIndex, isRepeating, show, length]);

  const next = () => {
    if (isRepeating || currentIndex < length - show) {
      handleCurrentIndex && handleCurrentIndex(currentIndex + 1);

      setCurrentIndex(prevState => prevState + 1);
    }
  };

  const prev = () => {
    if (isRepeating || currentIndex > 0) {
      handleCurrentIndex && handleCurrentIndex(currentIndex - 1);
      setCurrentIndex(prevState => prevState - 1);
    }
  };

  const handleTouchStart = e => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  };

  const handleTouchMove = e => {
    const touchDown = touchPosition;

    if (touchDown === null) {
      return;
    }

    const currentTouch = e.touches[0].clientX;
    const diff = touchDown - currentTouch;

    if (diff > 5) {
      next();
    }

    if (diff < -5) {
      prev();
    }

    setTouchPosition(null);
  };

  const handleTransitionEnd = () => {
    if (isRepeating) {
      if (currentIndex === 0) {
        setTransitionEnabled(false);
        setCurrentIndex(length);
      } else if (currentIndex === length + show) {
        setTransitionEnabled(false);
        setCurrentIndex(show);
      }
    }
  };

  const renderExtraPrev = () => {
    let output = [];
    for (let index = 0; index < show; index++) {
      output.push(children[length - 1 - index]);
    }
    output.reverse();
    return output;
  };

  const renderExtraNext = () => {
    let output = [];
    for (let index = 0; index < show; index++) {
      output.push(children[index]);
    }
    return output;
  };
  const classes = useStyles();
  return (
    <div className="carousel-container">
      <div className="carousel-wrapper">
        {/* You can alwas change the content of the button to other things */}
        {(isRepeating || currentIndex > 0) && (
          <IconButton onClick={prev} className={classes.leftArrow}>
            <ArrowBackIos />
          </IconButton>
        )}
        <div className="carousel-content-wrapper" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
          <div
            className={`carousel-content show-${show}`}
            style={{
              transform: `translateX(-${currentIndex * (100 / show)}%)`,
              transition: !transitionEnabled ? 'none' : undefined
            }}
            onTransitionEnd={() => handleTransitionEnd()}
          >
            {length > show && isRepeating && renderExtraPrev()}
            {children}
            {length > show && isRepeating && renderExtraNext()}
          </div>
        </div>
        {/* You can alwas change the content of the button to other things */}
        {(isRepeating || currentIndex < length - show) && (
          <IconButton onClick={next} className={classes.rightArrow}>
            <ArrowForwardIos />
          </IconButton>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  leftArrow: {
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '24px',
    backgroundColor: '#fff',
    border: `1px solid #fff`,
    left: '24px',
    paddingLeft: '1rem',
    '&:hover': {
      backgroundColor: '#ddd'
    },
    '@media (max-width: 767px)': {
      display: 'none'
    }
  },
  rightArrow: {
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '24px',
    backgroundColor: '#fff',
    border: `1px solid #fff`,
    right: '24px',

    '&:hover': {
      backgroundColor: '#ddd'
    },
    '@media (max-width: 767px)': {
      display: 'none'
    }
  }
}));
